import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton, tokens, useNotify } from '@hotjar/hotsauce';
import { useCurrentUser } from '@hotjar/user';

import { PreviousButton } from '@hotjar/webapp/features/Playbacks/Controls/PreviousButton';
import { NextButton } from '@hotjar/webapp/features/Playbacks/Controls/NextButton';
import { noop } from '@hotjar/webapp/lib/function';
import { getIsRetakerMode } from '@hotjar/webapp/features/Playbacks/store/utils';
import {
  hasPreviousRecording,
  hasNextRecording,
} from '@hotjar/recordings/src/features/Playbacks/utils';
import { useSelector, useStore, useStoreState } from '@hotjar/recordings/src/store/player/context';
import { trackPlaylistToggle } from '@hotjar/webapp/features/Playbacks/Playlist/utils/playlistTracking';
import { ExpandCollapseButton } from './ExpandCollapseButton';
import { ShareButton } from './ShareButton';
import { DeleteButton } from './DeleteButton';
import { BackButton } from './BackButton';
import { ThumbsUpDownButton } from './ThumbsUpDownButton';
import { ShareModal } from '../ShareModal';
import { useRecordingsApi, STATUS } from '../useRecordingsApi';
import { useModalStore } from '../store/modalStore';
import { ModalStateEnum } from '../types';
import { trackClick as clickTracker } from '../utils/tracking';

const Divider = styled.div`
  height: 32px;
  width: 1px;
  background-color: ${tokens.color.neutral_300};
`;

const LeftSideButtons = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-start;
  align-items: center;

  gap: ${tokens.spacer.x1pt5};
`;

const RightSideButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  gap: ${tokens.spacer.x1pt5};
`;

const RecordingTitle = styled.span`
  ${tokens.text('subtitle2')};
  margin-left: ${tokens.spacer.x0pt5};

  line-height: 32px;
`;

const StyledPlaylistButton = styled(IconButton)`
  ${({ isOpen }) =>
    isOpen &&
    `&, &:hover {
    background: ${tokens.color.cobalt_200} }
    border-radius: ${tokens.radius.m};
  `};
`;

// eslint-disable-next-line react/prop-types
const PlaylistButton = ({ isLoadingUser }) => {
  const { t } = useTranslation();
  const { setPlaylistOpened } = useStoreState();
  const { user, recording, playlistOpened } = useSelector((state) => ({
    user: state.user,
    recording: state.recording,
    playlistOpened: state.playlistOpened,
  }));

  const onPlaylistToggle = useCallback(
    (value) => {
      setPlaylistOpened(value);
      trackPlaylistToggle(recording, Number(user?.user_id), value ? 'Expand' : 'Collapse');
    },
    [recording, user?.user_id, setPlaylistOpened],
  );

  return !playlistOpened ? (
    <StyledPlaylistButton
      size="s"
      kind="ghost"
      icon="list"
      title={t('common:recordings.player.playlist.open')}
      data-testid="expand-button"
      tooltipText={t('common:recordings.player.playlist.open')}
      onClick={() => onPlaylistToggle(true)}
      disabled={isLoadingUser}
      isOpen={false}
    />
  ) : (
    <StyledPlaylistButton
      size="s"
      kind="ghost"
      icon="list"
      title={t('common:recordings.player.playlist.close')}
      tooltipText={t('common:recordings.player.playlist.close')}
      data-testid="collapse-button"
      onClick={() => onPlaylistToggle(false)}
      disabled={isLoadingUser}
      isOpen
    />
  );
};

export const TopMenu = ({
  isReadOnly,
  isShared,
  isPlaying,
  play,
  pause,
  recording,
  onShare,
  playlistVisible,
  isTokenShared,
  worthWatchingAnswer,
  onWorthWatchingAnswerChange,
  openWorthWatchingTooltip,
  siteId,
  loaded,
  hideControls,
  hideBackButton,
  onPrevious,
  onNext,
}) => {
  const playerStore = useStore();
  const [isShareOpen, showShareModal] = useState(false);
  const [hasPrev, setHasPreviousRecording] = useState(false);
  const [hasNext, setHasNextRecording] = useState(false);

  const [{ status }, { setWorthWatching }] = useRecordingsApi({
    siteId,
    recordingId: recording?.id,
  });

  const isLoadingOrNoRecording = !loaded || !recording;
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { showToast } = useNotify();
  const { t } = useTranslation();
  const { setActiveModal } = useModalStore.getState();

  const handleOnWorthWatchingAnswerChange = useCallback(
    async (newAnswer) => {
      const oldAnswer = worthWatchingAnswer;
      const isSuccess = await setWorthWatching(newAnswer);
      if (!isSuccess) {
        showToast({ kind: 'danger', text: t('common:player.recording-not-rated') });
        if (oldAnswer !== null) {
          onWorthWatchingAnswerChange(oldAnswer);
        }
        return;
      }
      onWorthWatchingAnswerChange(newAnswer);
    },
    [worthWatchingAnswer, setWorthWatching, onWorthWatchingAnswerChange, showToast, t],
  );

  const trackClick = !getIsRetakerMode() ? clickTracker : noop;
  const goToPreviousRecording = useCallback(() => {
    trackClick(recording, {
      element_name: 'playback_went_to_previous_recording',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_footer',
    });

    onPrevious();
  }, [onPrevious, recording, trackClick]);
  const goToNextRecording = useCallback(() => {
    trackClick(recording, {
      element_name: 'playback_went_to_next_recording',
      state_event_was_triggered_on: 'playback.player_navigation',
      ui_position: 'clicked_footer',
    });

    onNext();
  }, [onNext, recording, trackClick]);

  const { recordingsList } = useSelector((state) => ({ recordingsList: state.recordingsList }));

  useEffect(() => {
    setHasPreviousRecording(hasPreviousRecording(playerStore));
    setHasNextRecording(hasNextRecording(playerStore));
  }, [recordingsList, playerStore]);

  return (
    <div
      className="top-menu"
      css={css`
        display: flex;
        align-items: center;
        height: 48px;
        padding: 0 16px 0 8px;
        background-color: ${tokens.BRAND_COLORS.white};
        text-align: initial;
        border-bottom: 1px solid ${tokens.color.neutral_300};
      `}
    >
      {!isLoadingOrNoRecording && (
        <ShareModal
          isOpen={isShareOpen}
          isShared={isShared}
          recording={recording}
          onClose={() => showShareModal(false)}
          onShare={onShare}
          play={play}
          pause={pause}
          isPlaying={isPlaying}
          uiPosition="clicked-header-share-CTA"
        />
      )}

      <LeftSideButtons>
        {!hideBackButton ? (
          <>
            <BackButton
              isDisabled={isLoadingOrNoRecording || isLoadingUser}
              isLoggedIn={!!user && !isTokenShared}
              recording={recording}
            />
            <Divider />
          </>
        ) : null}

        {playlistVisible ? <PlaylistButton isLoadingUser={isLoadingUser} /> : null}

        {!isTokenShared && !hideControls && (
          <>
            {playlistVisible ? <Divider /> : null}
            <PreviousButton
              disabled={isLoadingOrNoRecording || !hasPrev}
              onClick={goToPreviousRecording}
            />

            <NextButton disabled={isLoadingOrNoRecording || !hasNext} onClick={goToNextRecording} />
          </>
        )}

        <RecordingTitle>
          {t('common:player.recording')} {recording?.id}
        </RecordingTitle>
      </LeftSideButtons>

      <RightSideButtons>
        {!hideControls && !isTokenShared && (
          <>
            {!isReadOnly && (
              <DeleteButton
                onClick={() => setActiveModal(ModalStateEnum.DELETE_RECORDING_OPEN)}
                disabled={isLoadingOrNoRecording}
              />
            )}
            <ThumbsUpDownButton
              answer={worthWatchingAnswer}
              onAnswerChange={handleOnWorthWatchingAnswerChange}
              isLoading={status === STATUS.LOADING}
              openTooltip={openWorthWatchingTooltip}
              disabled={isLoadingOrNoRecording}
            />
          </>
        )}
        {!isReadOnly && !isTokenShared && (
          <ShareButton onClick={() => showShareModal(true)} disabled={isLoadingOrNoRecording} />
        )}

        {!hideControls && (
          <>
            <Divider />
            <ExpandCollapseButton disabled={isLoadingOrNoRecording} />
          </>
        )}
      </RightSideButtons>
    </div>
  );
};

TopMenu.defaultProps = {
  worthWatchingAnswer: null,
  openWorthWatchingTooltip: false,
  recording: null,
  isTokenShared: false,
  siteId: null,
  loaded: false,
  hideControls: false,
  hideBackButton: false,
};

TopMenu.propTypes = {
  isReadOnly: PropTypes.bool.isRequired,
  isShared: PropTypes.bool.isRequired,
  isPlaying: PropTypes.bool.isRequired,
  recording: PropTypes.shape({
    id: PropTypes.number,
    site_features: PropTypes.arrayOf(PropTypes.string),
    effective_plan: PropTypes.string,
  }),
  play: PropTypes.func.isRequired,
  pause: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  isTokenShared: PropTypes.bool,
  playlistVisible: PropTypes.bool.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  worthWatchingAnswer: PropTypes.bool, // null is a valid value for not answered
  onWorthWatchingAnswerChange: PropTypes.func.isRequired,
  siteId: PropTypes.number,
  openWorthWatchingTooltip: PropTypes.bool,
  loaded: PropTypes.bool,
  hideControls: PropTypes.bool,
  hideBackButton: PropTypes.bool,
};
